import React, { forwardRef } from "react";
import PropTypes from "prop-types";

/**
 * FileInput component for dragging and dropping files
 */
class FileInputInner extends React.Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      isDragOver: false,
    };

    // Bind drag and drop event handlers
    this.handleDragEnter = this.handleDragEnter.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
  }

  handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: true });
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: false });
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: false });

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      // Create a synthetic event to match the expected format
      const syntheticEvent = {
        target: {
          files: files,
        },
      };
      this.props.onChange(syntheticEvent);
    }
  }

  /**
   * Render React Component
   * @returns {code}
   */
  render() {
    let {
      name = "file",
      disabled,
      accept,
      multiple,
      onChange = function () {
        return null;
      },
      style = {},
      className = "",
      children,
    } = this.props;

    // Merge instead of mutating: React 19 freezes prop objects in dev, and
    // the caller's style must stay untouched. position:relative anchors the
    // absolutely-positioned hidden input; callers may override display.
    const mergedStyle = { position: "relative", display: "inline-block", ...style };
    return (
      <div
        style={mergedStyle}
        // "dragging" lets the dropzone highlight while a drag hovers anywhere
        // over the zone (styled in sass/events/_draganddrop.scss).
        className={className + (this.state.isDragOver ? " dragging" : "")}
        onDragEnter={this.handleDragEnter}
        onDragLeave={this.handleDragLeave}
        onDragOver={this.handleDragOver}
        onDrop={this.handleDrop}
      >
        <input
          ref={this.props.forwardedRef || this.fileInputRef}
          type="file"
          name={name}
          onChange={onChange}
          disabled={disabled}
          accept={accept}
          multiple={!!multiple}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            opacity: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
          }}
        />
        {children}
      </div>
    );
  }
}

FileInputInner.propTypes = {
  /**
   * Form input name
   */
  name: PropTypes.string,
  /**
   * Input disabled
   */
  disabled: PropTypes.bool,
  /**
   * Mimetypes accepted
   */
  accept: PropTypes.string,
  /**
   * Allow selecting multiple files
   */
  multiple: PropTypes.bool,
  /**
   * Changes the input value
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Component style
   */
  style: PropTypes.object,
  /**
   * CSS class
   */
  className: PropTypes.string,
  /**
   * Child components
   */
  children: PropTypes.any,
  /**
   * Forwarded ref
   */
  forwardedRef: PropTypes.any,
};

// Export the component wrapped with forwardRef to handle React 19 ref compatibility
const FileInput = forwardRef((props, ref) => <FileInputInner {...props} forwardedRef={ref} />);

export default FileInput;
